import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Truck, FileText, Wallet, TrendingUp, Paperclip,
  AlertTriangle, Clock, CalendarClock, Image as ImageIcon,
} from "lucide-react";
import { fmtMoney, CURRENCIES } from "@/lib/money";
import PhotoUploader from "@/components/PhotoUploader";
import AttachmentThumb from "@/components/AttachmentThumb";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_SUP = { nombre: "", cif: "", direccion: "", telefono: "", email: "", banco: "", iban: "", notas: "" };
const EMPTY_BILL = { supplier_id: "", numero_proveedor: "", fecha: today(), vencimiento: "", oficina: "", moneda: "EUR", categoria: "transporte", notas: "", lineas: [{ descripcion: "", cantidad: 1, precio_unitario: 0, iva_pct: 21 }], photo_ids: [] };
const EMPTY_BPAY = { bill_id: "", fecha: today(), monto: 0, moneda: "EUR", metodo: "transferencia", referencia: "", photo_ids: [] };
const METHODS = ["transferencia", "efectivo", "tarjeta", "bizum", "cheque"];

const VENC_TONE = {
  vencida: "bg-red-100 text-red-700 border-red-200",
  proxima: "bg-amber-100 text-amber-700 border-amber-200",
  vigente: "bg-slate-100 text-slate-600 border-slate-200",
  sin_vencimiento: "bg-slate-100 text-slate-500 border-slate-200",
};

export const SuppliersTab = ({ oficinas = [], currency = "EUR" }) => {
  const { api, token } = useAuth();
  const { t } = useI18n();
  const [suppliers, setSuppliers] = useState([]);
  const [bills, setBills] = useState([]);
  const openBills = useMemo(() => bills.filter(b => b.estado !== "pagada"), [bills]);
  const [ranking, setRanking] = useState([]);
  const [payables, setPayables] = useState(null);
  const [proxDays, setProxDays] = useState(7);
  const [modal, setModal] = useState(null);
  const [sup, setSup] = useState(EMPTY_SUP);
  const [bill, setBill] = useState(EMPTY_BILL);
  const [bpay, setBpay] = useState(EMPTY_BPAY);
  const [busy, setBusy] = useState(false);
  const money = (v, m) => fmtMoney(v, m || currency);

  const load = useCallback(async () => {
    const [s, b, r, p] = await Promise.all([
      api.get("/suppliers"),
      api.get("/bills"),
      api.get("/expenses/providers").catch(() => ({ data: [] })),
      api.get("/accounting/payables/summary", { params: { dias_proximo: proxDays } }).catch(() => ({ data: null })),
    ]);
    setSuppliers(s.data || []);
    setBills(b.data || []);
    setRanking(r.data || []);
    setPayables(p.data);
  }, [api, proxDays]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const billTotals = useMemo(() => {
    const sub = bill.lineas.reduce((a, l) => a + (+l.cantidad || 0) * (+l.precio_unitario || 0), 0);
    const iva = bill.lineas.reduce((a, l) => a + (+l.cantidad || 0) * (+l.precio_unitario || 0) * (+l.iva_pct || 0) / 100, 0);
    return { sub, iva, total: sub + iva };
  }, [bill.lineas]);

  const save = async (path, body, reset) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success(t("saved")); setModal(null); reset(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const del = async (path) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(path); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  // Compute vencimiento status per bill (client side fallback)
  const hoy = today();
  const proxLimit = new Date(Date.now() + proxDays * 86400000).toISOString().slice(0, 10);
  const vencStatus = (b) => {
    const s = (b.saldo || 0);
    if (s <= 0.009) return "pagada";
    const due = (b.vencimiento || "").trim();
    if (!due) return "sin_vencimiento";
    if (due < hoy) return "vencida";
    if (due <= proxLimit) return "proxima";
    return "vigente";
  };

  return (
    <div className="space-y-4" data-testid="acc-suppliers">
      {/* === Payables dashboard === */}
      {payables && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="payables-dashboard">
          <div className="rounded-xl border-2 border-orange-200 bg-orange-50 p-4" data-testid="pay-total-card">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-orange-700">
              <Wallet className="w-3.5 h-3.5" /> {t("total_payable") || "Total por pagar"}
            </div>
            <div className="mt-1 text-2xl font-semibold text-orange-900 tabular-nums">
              {money(payables.total_por_pagar)}
            </div>
            <div className="mt-1 text-[11px] text-orange-800">
              {(payables.by_moneda || []).map((r) => `${money(r.total, r.moneda)}`).join(" · ") || "—"}
            </div>
          </div>
          <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4" data-testid="pay-overdue-card">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-red-700">
              <AlertTriangle className="w-3.5 h-3.5" /> {t("overdue") || "Vencido"}
            </div>
            <div className="mt-1 text-2xl font-semibold text-red-900 tabular-nums">
              {money(payables.total_vencido)}
            </div>
            <div className="mt-1 text-[11px] text-red-800">
              {(payables.vencidas || []).length} {t("purchase_invoices").toLowerCase()}
            </div>
          </div>
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4" data-testid="pay-upcoming-card">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-700">
              <CalendarClock className="w-3.5 h-3.5" /> {t("upcoming_dues") || `Próximos ${proxDays} días`}
            </div>
            <div className="mt-1 text-2xl font-semibold text-amber-900 tabular-nums">
              {money(payables.total_proximo)}
            </div>
            <div className="mt-1 text-[11px] text-amber-800 flex items-center gap-1">
              {(payables.proximos || []).length} {t("purchase_invoices").toLowerCase()}
              <select value={proxDays} onChange={(e) => setProxDays(+e.target.value)} data-testid="pay-prox-days"
                className="ms-auto bg-white/60 border border-amber-300 rounded px-1 py-0.5 text-[10px]">
                {[7, 15, 30].map(d => <option key={d} value={d}>{d}d</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Direct access to overdue / upcoming bills */}
      {payables && (payables.vencidas?.length > 0 || payables.proximos?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {payables.vencidas?.length > 0 && (
            <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-testid="overdue-list">
              <div className="px-4 py-2.5 border-b border-red-100 bg-red-50 text-xs font-bold uppercase tracking-wider text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" /> {t("overdue_bills") || "Facturas vencidas"} · {payables.vencidas.length}
              </div>
              <ul className="divide-y divide-red-100 max-h-[260px] overflow-y-auto">
                {payables.vencidas.slice(0, 20).map((v) => (
                  <li key={v.id} data-testid={`overdue-${v.id}`} className="px-4 py-2 flex items-center gap-3 text-sm">
                    <span className="text-[10px] font-bold rounded bg-red-600 text-white px-1.5 py-0.5">{v.dias_vencido}d</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{v.supplier_nombre} · <span className="font-mono">{v.numero_proveedor || "—"}</span></div>
                      <div className="text-[11px] text-slate-500">{v.vencimiento} · {v.oficina || "—"}</div>
                    </div>
                    <div className="text-end shrink-0">
                      <div className="tabular-nums font-semibold text-red-700">{money(v.saldo, v.moneda)}</div>
                      {v.photo_ids?.length > 0 && <div className="text-[10px] text-slate-400"><Paperclip className="w-3 h-3 inline" /> {v.photo_ids.length}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {payables.proximos?.length > 0 && (
            <div className="bg-white border border-amber-200 rounded-xl overflow-hidden" data-testid="upcoming-list">
              <div className="px-4 py-2.5 border-b border-amber-100 bg-amber-50 text-xs font-bold uppercase tracking-wider text-amber-700 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> {t("upcoming_bills") || "Próximos vencimientos"} · {payables.proximos.length}
              </div>
              <ul className="divide-y divide-amber-100 max-h-[260px] overflow-y-auto">
                {payables.proximos.slice(0, 20).map((v) => (
                  <li key={v.id} data-testid={`upcoming-${v.id}`} className="px-4 py-2 flex items-center gap-3 text-sm">
                    <span className="text-[10px] font-bold rounded bg-amber-600 text-white px-1.5 py-0.5">{v.vencimiento}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{v.supplier_nombre} · <span className="font-mono">{v.numero_proveedor || "—"}</span></div>
                      <div className="text-[11px] text-slate-500">{v.oficina || "—"}</div>
                    </div>
                    <div className="text-end shrink-0">
                      <div className="tabular-nums font-semibold text-amber-700">{money(v.saldo, v.moneda)}</div>
                      {v.photo_ids?.length > 0 && <div className="text-[10px] text-slate-400"><Paperclip className="w-3 h-3 inline" /> {v.photo_ids.length}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end flex-wrap gap-2">
        <button data-testid="new-supplier-btn" onClick={() => { setSup(EMPTY_SUP); setModal("sup"); }} className="bg-slate-900 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> {t("new_supplier")}</button>
        <button data-testid="new-bill-btn" onClick={() => { setBill({ ...EMPTY_BILL, lineas: [{ descripcion: "", cantidad: 1, precio_unitario: 0, iva_pct: 21 }], photo_ids: [] }); setModal("bill"); }} className="bg-orange-600 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> {t("new_bill")}</button>
        <button data-testid="new-bill-payment-btn" onClick={() => { setBpay({ ...EMPTY_BPAY, photo_ids: [] }); setModal("bpay"); }} className="bg-emerald-600 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> {t("new_bill_payment")}</button>
      </div>

      {/* --- Provider spend ranking --- */}
      {ranking.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl" data-testid="acc-sup-ranking">
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-orange-600" /> {t("provider_ranking") || "Ranking de gastos por proveedor"}
          </div>
          <ol className="divide-y divide-slate-100">
            {ranking.slice(0, 8).map((r, i) => (
              <li key={`${r.proveedor}-${r.moneda}`} data-testid={`rank-${i}`} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-orange-100 text-orange-700" : i < 3 ? "bg-slate-100 text-slate-600" : "bg-slate-50 text-slate-500"}`}>{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{r.proveedor}</div>
                  <div className="text-[11px] text-slate-500">{r.count} {t("expenses") || "gastos"} · {t("last") || "último"}: {r.last_date?.slice(0, 10) || "—"}</div>
                </div>
                <div className="text-end shrink-0">
                  <div className="font-mono tabular-nums font-semibold text-orange-700">{fmtMoney(r.total, r.moneda || currency)}</div>
                  <div className="text-[10px] text-slate-400 uppercase">{r.moneda || currency}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* --- Purchase invoices table with vencimiento status and attachments --- */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> {t("purchase_invoices")}</div>
        <table className="w-full text-sm min-w-[960px]">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="p-3 text-start">{t("bill_number")}</th>
              <th className="p-3 text-start">{t("supplier")}</th>
              <th className="p-3 text-start">{t("date")}</th>
              <th className="p-3 text-start">{t("due_date")}</th>
              <th className="p-3 text-start">{t("office")}</th>
              <th className="p-3 text-end">{t("total")}</th>
              <th className="p-3 text-end">{t("paid")}</th>
              <th className="p-3 text-end">{t("balance")}</th>
              <th className="p-3 text-start">{t("status")}</th>
              <th className="p-3 text-start">{t("attachment") || "Adjunto"}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bills.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-400">—</td></tr>}
            {bills.map(b => {
              const vs = vencStatus(b);
              return (
                <tr key={b.id} data-testid={`bill-row-${b.id}`}>
                  <td className="p-3 font-mono">{b.numero_proveedor || "—"}</td>
                  <td className="p-3">{b.supplier_nombre}</td>
                  <td className="p-3 tabular-nums text-slate-600">{b.fecha}</td>
                  <td className="p-3 tabular-nums">
                    {b.vencimiento || "—"}
                    {b.saldo > 0.009 && b.vencimiento && (
                      <span data-testid={`bill-venc-${b.id}`} className={`ms-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${VENC_TONE[vs] || VENC_TONE.vigente}`}>
                        {t(`venc_${vs}`) || vs}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-slate-600">{b.oficina || "—"}</td>
                  <td className="p-3 text-end tabular-nums font-semibold">{money(b.total, b.moneda)}</td>
                  <td className="p-3 text-end tabular-nums text-emerald-700">{money(b.pagado, b.moneda)}</td>
                  <td className="p-3 text-end tabular-nums">{money(b.saldo, b.moneda)}</td>
                  <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${b.estado === "pagada" ? "bg-emerald-100 text-emerald-700" : b.estado === "parcial" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{t(`inv_status_${b.estado}`) || b.estado}</span></td>
                  <td className="p-3"><AttachmentThumb api={api} token={token} ids={b.photo_ids || []} testid={`bill-att-${b.id}`} /></td>
                  <td className="p-3 text-end"><button data-testid={`bill-del-${b.id}`} onClick={() => del(`/bills/${b.id}`)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* --- Suppliers table --- */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> {t("suppliers")}</div>
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr><th className="p-3 text-start">{t("fullname")}</th><th className="p-3 text-start">{t("tax_id")}</th><th className="p-3 text-start">{t("email")}</th><th className="p-3 text-start">{t("phone")}</th><th className="p-3 text-start">IBAN</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {suppliers.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">—</td></tr>}
            {suppliers.map(s => (
              <tr key={s.id} data-testid={`sup-row-${s.id}`}>
                <td className="p-3 font-medium">{s.nombre}</td>
                <td className="p-3 text-slate-600">{s.cif || "—"}</td>
                <td className="p-3 text-slate-600">{s.email || "—"}</td>
                <td className="p-3 text-slate-600">{s.telefono || "—"}</td>
                <td className="p-3 font-mono text-xs text-slate-500">{s.iban || "—"}</td>
                <td className="p-3 text-end"><button data-testid={`sup-del-${s.id}`} onClick={() => del(`/suppliers/${s.id}`)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === "sup" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={(e) => { e.preventDefault(); save("/suppliers", sup, () => setSup(EMPTY_SUP)); }} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-3 my-8" data-testid="supplier-modal">
            <div className="flex items-center justify-between"><h3 className="font-display font-semibold text-lg">{t("new_supplier")}</h3><button type="button" onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div><label className={lbl}>{t("fullname")}</label><input required data-testid="sup-nombre" value={sup.nombre} onChange={(e) => setSup({ ...sup, nombre: e.target.value })} className={inp} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={lbl}>{t("tax_id")}</label><input data-testid="sup-cif" value={sup.cif} onChange={(e) => setSup({ ...sup, cif: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("phone")}</label><input value={sup.telefono} onChange={(e) => setSup({ ...sup, telefono: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("email")}</label><input type="email" value={sup.email} onChange={(e) => setSup({ ...sup, email: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("bank")}</label><input value={sup.banco} onChange={(e) => setSup({ ...sup, banco: e.target.value })} className={inp} /></div>
            </div>
            <div><label className={lbl}>{t("address")}</label><input value={sup.direccion} onChange={(e) => setSup({ ...sup, direccion: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>IBAN</label><input value={sup.iban} onChange={(e) => setSup({ ...sup, iban: e.target.value })} className={inp} /></div>
            <div className="flex gap-3 pt-1"><button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button><button type="submit" disabled={busy} data-testid="sup-submit" className="flex-1 bg-slate-900 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button></div>
          </form>
        </div>
      )}

      {modal === "bill" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <form onSubmit={(e) => { e.preventDefault(); save("/bills", bill, () => setBill(EMPTY_BILL)); }} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-3 my-8" data-testid="bill-modal">
            <div className="flex items-center justify-between"><h3 className="font-display font-semibold text-lg">{t("new_bill")}</h3><button type="button" onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("supplier")}</label>
                <select required data-testid="bill-supplier" value={bill.supplier_id} onChange={(e) => setBill({ ...bill, supplier_id: e.target.value })} className={inp}>
                  <option value="">—</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                </select>
              </div>
              <div><label className={lbl}>{t("bill_number")}</label><input data-testid="bill-numero" value={bill.numero_proveedor} onChange={(e) => setBill({ ...bill, numero_proveedor: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("date")}</label><input required type="date" data-testid="bill-fecha" value={bill.fecha} onChange={(e) => setBill({ ...bill, fecha: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("due_date")}</label><input type="date" data-testid="bill-venc" value={bill.vencimiento} onChange={(e) => setBill({ ...bill, vencimiento: e.target.value })} className={inp} /></div>
              <div>
                <label className={lbl}>{t("office")}</label>
                <select data-testid="bill-oficina" value={bill.oficina} onChange={(e) => setBill({ ...bill, oficina: e.target.value })} className={inp}>
                  <option value="">—</option>
                  {oficinas.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>{t("category")}</label>
                <select value={bill.categoria} onChange={(e) => setBill({ ...bill, categoria: e.target.value })} className={inp}>
                  {["transporte", "aduanas", "combustible", "alquiler", "suministros", "otros"].map(c => <option key={c} value={c}>{t(`exp_cat_${c}`) || c}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>{t("currency")}</label>
                <select data-testid="bill-moneda" value={bill.moneda} onChange={(e) => setBill({ ...bill, moneda: e.target.value })} className={inp}>
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} · {c.symbol}</option>)}
                </select>
              </div>
            </div>
            <div className="border-2 border-orange-200 rounded-lg p-3 bg-orange-50/40">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-orange-900">{t("invoice_lines")}</p>
                <button type="button" data-testid="bill-add-line" onClick={() => setBill(f => ({ ...f, lineas: [...f.lineas, { _uid: `bl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, descripcion: "", cantidad: 1, precio_unitario: 0, iva_pct: 21 }] }))} className="bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"><Plus className="w-3 h-3" /> {t("add")}</button>
              </div>
              <div className="space-y-2">
                {bill.lineas.map((l, idx) => (
                  <div key={l._uid || `bl-${idx}`} className="bg-white border border-orange-200 rounded-md p-2 grid grid-cols-[1fr_58px_84px_58px_28px] gap-2 items-center">
                    <input required placeholder={t("description")} data-testid={`bill-line-${idx}-desc`} value={l.descripcion} onChange={(e) => setBill(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], descripcion: e.target.value }; return { ...f, lineas: a }; })} className={inp} />
                    <input type="number" step="0.01" title={t("qty")} value={l.cantidad} onChange={(e) => setBill(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], cantidad: +e.target.value }; return { ...f, lineas: a }; })} className={`${inp} tabular-nums`} />
                    <input type="number" step="0.01" title={t("unit_price")} data-testid={`bill-line-${idx}-price`} value={l.precio_unitario} onChange={(e) => setBill(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], precio_unitario: +e.target.value }; return { ...f, lineas: a }; })} className={`${inp} tabular-nums`} />
                    <input type="number" step="0.5" title={t("vat")} value={l.iva_pct} onChange={(e) => setBill(f => { const a = [...f.lineas]; a[idx] = { ...a[idx], iva_pct: +e.target.value }; return { ...f, lineas: a }; })} className={`${inp} tabular-nums`} />
                    <button type="button" onClick={() => setBill(f => ({ ...f, lineas: f.lineas.filter((_, i) => i !== idx) }))} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-orange-200 flex justify-end gap-4 text-xs text-orange-900">
                <span>{t("subtotal")}: <b className="tabular-nums">{money(billTotals.sub)}</b></span>
                <span>{t("vat")}: <b className="tabular-nums">{money(billTotals.iva)}</b></span>
                <span>{t("total")}: <b className="tabular-nums" data-testid="bill-total">{money(billTotals.total)}</b></span>
              </div>
            </div>
            <div><label className={lbl}>{t("notes")}</label><input value={bill.notas} onChange={(e) => setBill({ ...bill, notas: e.target.value })} className={inp} /></div>
            <div>
              <PhotoUploader
                scope="bill" scopeId=""
                value={bill.photo_ids || []}
                onChange={(ids) => setBill({ ...bill, photo_ids: ids })}
                max={4}
                label={t("attach_bill") || "Adjuntar factura / imagen"}
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                testid="bill-attach"
              />
            </div>
            <div className="flex gap-3 pt-1"><button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button><button type="submit" disabled={busy} data-testid="bill-submit" className="flex-1 bg-orange-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button></div>
          </form>
        </div>
      )}

      {modal === "bpay" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={(e) => { e.preventDefault(); save("/bill-payments", bpay, () => setBpay(EMPTY_BPAY)); }} className="bg-white rounded-xl w-full max-w-md p-6 space-y-3 my-8" data-testid="bill-payment-modal">
            <div className="flex items-center justify-between"><h3 className="font-display font-semibold text-lg flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> {t("new_bill_payment")}</h3><button type="button" onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div>
              <label className={lbl}>{t("purchase_invoice")}</label>
              <select required data-testid="bpay-bill" value={bpay.bill_id} onChange={(e) => { const b = bills.find(x => x.id === e.target.value); setBpay({ ...bpay, bill_id: e.target.value, monto: b ? b.saldo : bpay.monto, moneda: b ? b.moneda : bpay.moneda }); }} className={inp}>
                <option value="">—</option>
                {openBills.map(b => <option key={b.id} value={b.id}>{b.numero_proveedor || "—"} · {b.supplier_nombre} · {t("balance")} {money(b.saldo, b.moneda)}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>{t("date")}</label><input required type="date" value={bpay.fecha} onChange={(e) => setBpay({ ...bpay, fecha: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("amount")}</label><input required type="number" step="0.01" data-testid="bpay-monto" value={bpay.monto} onChange={(e) => setBpay({ ...bpay, monto: +e.target.value })} className={inp} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>{t("payment_method")}</label><select value={bpay.metodo} onChange={(e) => setBpay({ ...bpay, metodo: e.target.value })} className={inp}>{METHODS.map(m => <option key={m} value={m}>{t(`pay_${m}`) || m}</option>)}</select></div>
              <div><label className={lbl}>{t("reference")}</label><input value={bpay.referencia} onChange={(e) => setBpay({ ...bpay, referencia: e.target.value })} className={inp} /></div>
            </div>
            <div>
              <PhotoUploader
                scope="payment" scopeId=""
                value={bpay.photo_ids || []}
                onChange={(ids) => setBpay({ ...bpay, photo_ids: ids })}
                max={4}
                label={t("attach_receipt") || "Adjuntar recibo"}
                testid="bpay-attach"
              />
            </div>
            <div className="flex gap-3 pt-1"><button type="button" onClick={() => setModal(null)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button><button type="submit" disabled={busy} data-testid="bpay-submit" className="flex-1 bg-emerald-600 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button></div>
          </form>
        </div>
      )}
    </div>
  );
};
